/**
 * Frontend tracing for the company portal.
 *
 * Spans go to the API Worker's `/api/telemetry/traces` proxy, which attaches
 * the Cloudflare Access service token server-side — the token must never be
 * shipped to a browser. The portal is served from company.automadoc.cc and the
 * API from api.automadoc.cc, so this is a cross-origin POST; both hostnames
 * match the `.automadoc.cc` suffix the Worker's CORS allowlist accepts, and
 * `traceparent` is in its allowHeaders, without which the preflight would fail
 * and every portal span would arrive as a disconnected root.
 *
 * Disabled unless VITE_OTEL_ENABLED is set, so local dev does not write into
 * the shared 14-day ClickHouse volume.
 */

import { BrowserTracing } from '@lark-automadoc/shared-types/otel/browser'

let tracing: BrowserTracing | null = null

/** Must match an entry in the proxy's BROWSER_SERVICES allowlist. */
const SERVICE_NAME = 'automadoc-company-ui'

const API_BASE = import.meta.env.VITE_API_URL || ''

export function initTelemetry(): void {
  if (tracing) return
  if (import.meta.env.VITE_OTEL_ENABLED !== 'true') return

  try {
    tracing = new BrowserTracing({
      serviceName: SERVICE_NAME,
      endpoint: `${API_BASE}/api/telemetry/traces`,
      sampleRate: Number(import.meta.env.VITE_OTEL_SAMPLE_RATE ?? '1.0'),
      environment: import.meta.env.MODE,
      // Only the API origin. Paddle's checkout and portal endpoints must not
      // receive traceparent: it is not in their CORS allowHeaders, so the
      // preflight would fail and the purchase would break.
      propagateTo: propagationOrigins(),
    })

    tracing.recordPageLoad()
    tracing.installErrorHandlers()
    tracing.installFlushOnHide()
  } catch {
    // Telemetry setup must never prevent the app from starting.
    tracing = null
  }
}

function propagationOrigins(): string[] {
  try {
    // Empty API_BASE means the Vite proxy, i.e. same origin.
    return [new URL(API_BASE || window.location.href).origin]
  } catch {
    return []
  }
}

/**
 * Start a trace for one user-visible operation. Returns undefined when
 * telemetry is off or the trace was not sampled — callers use `?.`.
 */
export function startTrace(name: string, attributes?: Record<string, string | number | boolean | undefined>) {
  return tracing?.startTrace(name, attributes)
}

/**
 * Trace headers for an outbound API call, so the Worker span becomes a child
 * of the browser span instead of a disconnected root.
 */
export function traceHeaders(span: ReturnType<typeof startTrace>, url: string): Record<string, string> {
  if (!tracing || !span || !tracing.shouldPropagateTo(url)) return {}
  const traceparent = tracing.traceparentFor(span)
  return traceparent ? { traceparent } : {}
}
